/**
 * One styling entry as the composer stores it. Shared by every component that
 * renders inline markup, so nothing here knows about text.
 *
 * Two fields rather than one because they fail differently. Class names are
 * names of rules the customer's own CSS owns, and this library can neither see
 * them nor be sure they exist. Declarations are CSS this library has to
 * understand well enough to hand to a platform that wants an object rather
 * than a string.
 */
export type SpanStyleItem = {
  /** Author-facing name for identifying the entry in the composer. Never rendered. */
  label?: string;
  /** Whitespace-separated class names, exactly as the author typed them. */
  classNames?: string;
  /** A CSS declaration list — `color: #c00; font-weight: 600` — without braces. */
  declarations?: string;
};

/**
 * Runtime guard for entries arriving through componentAttributes (untyped,
 * editor-authored data).
 *
 * Only object shape is checked, for the same reason as `isLinkItem`: one bad
 * field must cost that field rather than the whole entry, so per-field
 * validation belongs to `resolveSpanStyleAttributes` where each value is turned
 * into something a platform can apply.
 */
export function isSpanStyleItem(value: unknown): value is SpanStyleItem {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True for a cleared slot. Clearing an entry stores `{}` rather than removing
 * the array element, because marker indexes are 1-based positions: dropping an
 * element would silently repoint every later marker.
 *
 * A `label` alone is a cleared slot. It is the author's note to themselves
 * about which entry this is, and an entry that only has a name applies nothing.
 */
export function isEmptySpanStyleItem(item: unknown): boolean {
  if (!isSpanStyleItem(item)) {
    return true;
  }
  return [item.classNames, item.declarations].every(
    (value) => typeof value !== 'string' || value.trim() === ''
  );
}

/**
 * The entry a `{.n|…}` marker points at, by 1-based position. Tolerant of bad
 * data on every axis — a non-array list, a fractional or out-of-range index, a
 * hole in the array — because none of it may throw during a render.
 */
export function getSpanStyleItemAt(
  spanStyles: unknown,
  oneBasedIndex: number
): SpanStyleItem | undefined {
  if (!Array.isArray(spanStyles)) {
    return undefined;
  }
  if (!Number.isInteger(oneBasedIndex) || oneBasedIndex < 1) {
    return undefined;
  }
  const entry = spanStyles[oneBasedIndex - 1];
  return isSpanStyleItem(entry) ? entry : undefined;
}
